import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ Error en React Component:', error, errorInfo)

    this.setState({
      error,
      errorInfo,
      errorId: Date.now().toString(36)
    })

    // Opcional: enviar error a un servicio de logging
    // logErrorToService(error, errorInfo, this.state.errorId)
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    })
  }

  render() {
    if (this.state.hasError) {
      // Verificar si es un error específico que conocemos
      const isNetworkError = this.state.error?.message?.includes('Network') ||
        this.state.error?.message?.includes('fetch')
      const isServiceWorkerError = this.state.error?.message?.includes('Service Worker') ||
        this.state.error?.message?.includes('cache')

      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
            {/* Icono de error */}
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>

            {/* Mensaje principal */}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Ups! Algo salió mal
            </h1>

            <p className="text-gray-600 mb-6">
              La aplicación encontró un error inesperado. Intenta recargar la página.
            </p>

            {/* Detalles del error (solo en desarrollo) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm text-red-600 font-medium mb-2">
                  Ver detalles técnicos
                </summary>
                <div className="bg-gray-50 rounded p-3 mt-2 text-xs">
                  <div className="mb-2">
                    <strong>Error ID:</strong> {this.state.errorId}
                  </div>
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                  <div className="mb-2">
                    <strong>Component:</strong> {this.state.errorInfo.componentStack?.split('\n')[1]?.trim()}
                  </div>
                  <div>
                    <strong>Stack:</strong>
                    <pre className="whitespace-pre-wrap text-gray-600">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                </div>
              </details>
            )}

            {/* Mensajes específicos según tipo de error */}
            {isNetworkError && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  🔌 Parece que hay problemas de conexión. Verifica tu internet.
                </p>
              </div>
            )}

            {isServiceWorkerError && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  ⚙️ Error en el sistema de caché. Recargar la página debería solucionarlo.
                </p>
              </div>
            )}

            {/* Botones de acción */}
            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reintentar</span>
              </button>

              <button
                onClick={() => window.location.reload()}
                className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Recargar página completa
              </button>
            </div>

            {/* Información de contacto */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Si el problema persiste, contacta al soporte
              </p>
              {this.state.errorId && (
                <p className="text-xs text-gray-500">
                  Código de error: <span className="font-mono">{this.state.errorId}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Componente para errores específicos de Firebase
export const FirebaseErrorBoundary = ({ children }) => {
  return (
    <ErrorBoundary
      fallback={({ error, retry }) => (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🔥</span>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Error de Conexión
            </h2>

            <p className="text-gray-600 mb-4">
              Hubo un problema al conectar con los servicios de Wyra.
            </p>

            {error?.message?.includes('CORS') && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  Error de configuración. Recarga la página o contacta soporte.
                </p>
              </div>
            )}

            <button
              onClick={retry}
              className="w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
            >
              Reintentar conexión
            </button>
          </div>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  )
}

export default ErrorBoundary